import { useEffect, useRef, useState } from "react";

interface CheckOverflowHookProps {
  ignoreObserver?: boolean;
  handleOnOverflow?: (ref: HTMLElement) => void;
  handleOnNotOverflow?: (ref: HTMLElement) => void;
}

export const useCheckOverflow = <ElementT extends HTMLElement>({
  ignoreObserver,
  handleOnOverflow,
  handleOnNotOverflow,
}: CheckOverflowHookProps) => {
  const ref = useRef<ElementT>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (!ref.current) {
        return;
      }

      const element = ref.current;

      // Check both horizontal and vertical overflow
      // This works with line-clamp because scrollHeight still reflects full content height
      const isOverflowingContent =
        element.scrollWidth > element.clientWidth || element.scrollHeight > element.clientHeight;

      setIsOverflowing(isOverflowingContent);

      if (isOverflowingContent) {
        handleOnOverflow?.(element);
      } else {
        handleOnNotOverflow?.(element);
      }
    };

    checkOverflow();

    if (!ignoreObserver && ref.current) {
      const observer = new ResizeObserver(checkOverflow);
      observer.observe(ref.current);

      return () => {
        observer.disconnect();
      };
    }
  }, [handleOnNotOverflow, handleOnOverflow, ignoreObserver]);

  return {
    ref,
    isOverflowing,
  };
};
